import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { errorMessages } from '~/constants/errorMessages.ts';
import api from '~/libs/api';
import { TCurrency } from './types';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';
import { currencyCodes } from '~/constants/common.ts';
import { useSelector } from 'react-redux';
import { RootState } from '~/store/store';

const getCurrencies = (token: string) => async () => {
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  try {
    const { data } = await api.get(`/dictionary-service/currency`, { headers });
    return data;
  } catch {
    throw Error(errorMessages.SERVICE_UNAVAILABLE);
  }
};

export const useCurrenciesQuery = () => {
  const token = useSelector((state: RootState) => state.users.token);
  const { data, error, isLoading, isError } = useQuery<TCurrency[]>({
    queryKey: ['currency'],
    queryFn: getCurrencies(token),
    staleTime: 30 * 60 * 1000,
  });

  const currencyOptions: TAutocompleteOption[] = useMemo(() => {
    if (!data) return [];
    return data
      .filter((currency) => Object.values(currencyCodes).includes(currency.id))
      .map((currency) => ({
        value: currency.id.toString(),
        label: currency.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  return { currencyOptions, currencies: data, error, isLoading, isError };
};
